import React from "react"
import {
  CContainer,
  CRow,
  CCol,
  CBadge,
  CButton,
} from "@coreui/react"

import { CIcon } from "@coreui/icons-react"
import {
  cilUser,
  cilEnvelopeOpen,
  cilShieldAlt,
  cilAccountLogout,
  cilPeople,
  cilChevronRight,
} from "@coreui/icons"
import { Sun, Moon } from "lucide-react"

import "./ProfilePage.css"
import { useAuth } from "../hooks/useAuth"
import { useTheme } from "../utils/theme"
import { useNavigate } from "react-router-dom"
import CustomSwitch from "../components/CustomSwitch"

const ProfilePage = () => {
  const { user, logout } = useAuth()
  const { mode, toggleTheme } = useTheme()
  const isDark = mode === "dark"
  const navigate = useNavigate()

  if (!user) return null

  const handleLogout = async () => {
    await logout()
    navigate("/login")
  }

  return (
    <CContainer className="profile-page">

      <CRow className="justify-content-center">
        <CCol md={6} lg={5}>

          <div className="profile-card">

            {/* HEADER */}
            <div className="profile-header">
              <div className="profile-avatar">
                {user.username?.charAt(0).toUpperCase()}
              </div>
              <h5>{user.username}</h5>
              <small>User Profile</small>
            </div>

            {/* BODY */}
            <div className="profile-body">

              <div className="d-flex flex-column gap-1">

                {/* Username */}
                <div className="profile-row">
                  <div className="profile-row-label">
                    <CIcon icon={cilUser} />
                    <span>Username</span>
                  </div>
                  <span className="profile-row-value">{user.username}</span>
                </div>

                {/* Email */}
                <div className="profile-row">
                  <div className="profile-row-label">
                    <CIcon icon={cilEnvelopeOpen} />
                    <span>Email</span>
                  </div>
                  <span className="profile-row-value">{user.email}</span>
                </div>

                {/* Role */}
                <div className="profile-row">
                  <div className="profile-row-label">
                    <CIcon icon={cilShieldAlt} />
                    <span>Role</span>
                  </div>

                  <CBadge color="info" shape="rounded-pill">
                    {user.role}
                  </CBadge>
                </div>

              </div>

              {/* PREFERENCES */}
              <hr className="my-4" />

              <div className="profile-section-label">Preferences</div>

              <div className="d-flex flex-column gap-1">

                {/* Dark mode toggle — moved here from the sidebar */}
                <div
                  className="profile-row profile-row-action"
                  role="button"
                  onClick={toggleTheme}
                >
                  <div className="profile-row-label">
                    {isDark ? <Moon size={16} /> : <Sun size={16} />}
                    <span>{isDark ? "Dark mode" : "Light mode"}</span>
                  </div>

                  <CustomSwitch
                    size="small"
                    checked={isDark}
                    onChange={toggleTheme}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>

              </div>

              {/* ADMIN */}
              {user.role === "admin" && (
                <>
                  <hr className="my-4" />

                  <div className="profile-section-label">Admin</div>

                  <div className="d-flex flex-column gap-1">
                    {/* Manage Users — moved here from the sidebar */}
                    <button
                      type="button"
                      className="profile-row profile-row-action profile-row-link"
                      onClick={() => navigate("/admin/users")}
                    >
                      <div className="profile-row-label">
                        <CIcon icon={cilPeople} />
                        <span>Manage Users</span>
                      </div>

                      <CIcon icon={cilChevronRight} className="profile-row-chevron" />
                    </button>
                  </div>
                </>
              )}

              {/* LOGOUT */}
              <hr className="my-4" />

              <CButton
                color="danger"
                variant="outline"
                className="w-100 d-flex align-items-center justify-content-center gap-2"
                onClick={handleLogout}
              >
                <CIcon icon={cilAccountLogout} />
                Logout
              </CButton>

            </div>

          </div>

        </CCol>
      </CRow>

    </CContainer>
  )
}

export default ProfilePage
